/** Opaque intent identifies a quote/amount context, never an account.
 * It used to travel in the book URL (`forq_intent`) and landed in their logs.
 * Now it stays in the promise object and the tab binding. `withIntent` is
 * only for our own matching, never for the address the book receives. */
export const INTENT_PARAM = 'forq_intent';

const INTENT_ID = /^[a-zA-Z0-9_-]{16,80}$/;

export function parseIntent(value: string | null | undefined): string | null {
  if (value == null) return null;
  if (value === '') return '';
  return INTENT_ID.test(value) ? value : '';
}

/** null = legacy URL; empty = malformed marker, never fall back to legacy. */
export function intentOf(href: string): string | null {
  try {
    return parseIntent(new URL(href).searchParams.get(INTENT_PARAM));
  } catch { return ''; }
}

export function withIntent(href: string, id: string): string {
  const url = new URL(href);
  url.searchParams.set(INTENT_PARAM, id);
  return url.toString();
}

/** Book navigation must not carry our marker — they log the query string. */
export function withoutIntent(href: string): string {
  try {
    const url = new URL(href);
    if (!url.searchParams.has(INTENT_PARAM)) return href;
    url.searchParams.delete(INTENT_PARAM);
    return url.toString();
  } catch { return href; }
}

export function sameIntent(want: string, have: string): boolean {
  const id = intentOf(want);
  return id !== '' && id === intentOf(have);
}

/** Both marked — same id. One unmarked — the book dropped the tail. */
export function intentFits(want: string, have: string): boolean {
  const a = intentOf(want);
  const b = intentOf(have);
  if (a === '' || b === '') return false;
  if (a && b) return a === b;
  return true;
}

const issued = new Map<string, string>();
/** Stable across quote refreshes in this document, unique across tabs/feeds. */
export function intentFor(fork: string): string {
  // Both legs belong to one action; venue identity is checked separately.
  const key = fork;
  let id = issued.get(key);
  if (!id) id = crypto.randomUUID();
  issued.delete(key);
  issued.set(key, id);
  if (issued.size > 10000) issued.delete(issued.keys().next().value!);
  return id;
}
